/**
 * Exibe metadados de mensagens interativas do WhatsApp (botões/lista) a partir de `json_whatsapp`.
 * Cobre webhook Meta (entry.changes.value.messages) e payload “cru” da API Fácil (espelhado em json_whatsapp).
 */

function parseJsonMaybe(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function metaInboundMessage(parsed) {
  const entry = parsed?.entry;
  const e0 = Array.isArray(entry) ? entry[0] : null;
  const changes = e0?.changes;
  const ch0 = Array.isArray(changes) ? changes[0] : null;
  const msgs = ch0?.value?.messages;
  return Array.isArray(msgs) ? msgs[0] : null;
}

const TECH_ID = /^(btn_\d+|ura_row_\d+|ura_opt_\d+)$/i;

export function isTechnicalWhatsappChoiceId(s) {
  if (s == null) return false;
  return TECH_ID.test(String(s).trim());
}

/** Remove envelope xpro_group / entry para inspecionar o corpo do provedor. */
function apifacilBodyRoot(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  const { xpro_group: _xg, entry: _e, ...rest } = parsed;
  return rest;
}

function pickFirstString(obj, keys) {
  if (!obj || typeof obj !== "object") return "";
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return "";
}

/**
 * Tenta achar rótulo humano no mesmo JSON quando `mensagem` / message.text é só id técnico.
 */
export function humanLabelFromApifacilPayload(parsed) {
  const b = apifacilBodyRoot(parsed);
  if (!b) return "";
  const candidates = [
    pickFirstString(b, [
      "titulo_selecionado",
      "texto_botao",
      "titulo_botao",
      "nome_botao",
      "botao_texto",
      "selected_title",
      "button_text",
      "lista_titulo",
      "row_title",
      "resposta_botao",
      "valor_botao",
      "opcao_selecionada",
      "item_titulo",
      "resposta",
      "texto_resposta",
      "conteudo_botao",
    ]),
  ];
  const dados = b.dados || b.data || b.payload;
  if (dados && typeof dados === "object") {
    candidates.push(
      pickFirstString(dados, [
        "texto_botao",
        "titulo_botao",
        "titulo",
        "texto",
        "title",
        "nome_botao",
        "selected_title",
      ])
    );
  }
  const rawMsg = String(b.mensagem || "").trim();
  for (const c of candidates) {
    if (!c) continue;
    if (c === rawMsg) continue;
    if (isTechnicalWhatsappChoiceId(c)) continue;
    return c;
  }
  return "";
}

/**
 * @returns {{ kind: string, title: string, id: string } | null}
 */
export function metaInboundInteractiveChoice(parsed) {
  const waMsg = metaInboundMessage(parsed);
  if (!waMsg || typeof waMsg !== "object") return null;
  if (waMsg.type !== "interactive" || !waMsg.interactive) return null;
  const ir = waMsg.interactive;
  if (ir.type === "button_reply" && ir.button_reply) {
    const br = ir.button_reply;
    const id = String(br.id || "").trim();
    const title = String(br.title != null ? br.title : "").trim();
    return {
      kind: "button_reply",
      title: title || id,
      id: id || title,
    };
  }
  if (ir.type === "list_reply" && ir.list_reply) {
    const lr = ir.list_reply;
    const id = String(lr.id || "").trim();
    const title = String(lr.title != null ? lr.title : "").trim();
    return {
      kind: "list_reply",
      title: title || id,
      id: id || title,
    };
  }
  return null;
}

/**
 * Extrai botões / linhas de lista de um payload de envio Meta (se armazenado em json_whatsapp).
 */
export function extractMetaStyleOutboundOptions(parsed) {
  if (!parsed || typeof parsed !== "object") return [];
  const inter = parsed.interactive && typeof parsed.interactive === "object" ? parsed.interactive : null;
  if (!inter) return [];
  const out = [];
  if (inter.type === "button" && inter.action?.buttons && Array.isArray(inter.action.buttons)) {
    for (const bt of inter.action.buttons) {
      const reply = bt?.reply && typeof bt.reply === "object" ? bt.reply : null;
      const id = String(reply?.id || "").trim();
      const title = String(reply?.title || "").trim();
      if (title || id) out.push({ id: id || title, title: title || id });
    }
  }
  if (inter.type === "list" && inter.action?.sections && Array.isArray(inter.action.sections)) {
    for (const sec of inter.action.sections) {
      const rows = Array.isArray(sec?.rows) ? sec.rows : [];
      for (const row of rows) {
        const id = String(row?.id || "").trim();
        const title = String(row?.title || "").trim();
        if (title || id) out.push({ id: id || title, title: title || id, section: sec?.title || "" });
      }
    }
  }
  return out;
}

/** Botões no formato API Fácil / enviar-botao (id + text). */
export function extractApifacilStyleButtons(parsed) {
  const b = apifacilBodyRoot(parsed);
  if (!b || !Array.isArray(b.buttons)) return [];
  return b.buttons
    .map((x) => ({
      id: String(x?.id || "").trim(),
      title: String(x?.text || x?.title || "").trim(),
    }))
    .filter((x) => x.title || x.id);
}

/**
 * URA com muitas opções: corpo do texto inclui linhas "1. Rótulo".
 * @returns {string[]}
 */
export function parseNumberedLabelsFromUraOverflowText(text) {
  if (!text || typeof text !== "string") return [];
  const idx = text.indexOf("\n\n");
  const tail = idx >= 0 ? text.slice(idx + 2) : "";
  if (!tail.trim()) return [];
  const lines = tail.split(/\r?\n/);
  const labels = [];
  for (const line of lines) {
    const m = /^\s*(\d+)\.\s+(.+)$/.exec(line);
    if (m) labels.push(String(m[2]).trim());
  }
  return labels;
}

function senderIsClient(message) {
  const st = (message?.senderType || "").toLowerCase();
  return st === "client" || st === "customer" || message?.channel === "customer";
}

/**
 * Mapeia btn_0 → primeiro rótulo da lista numerada na última mensagem de agente anterior.
 */
export function resolveChoiceFromPriorNumberedList(message, messageIndex, allMessages) {
  const t = message?.text != null ? String(message.text).trim() : "";
  if (!t || !isTechnicalWhatsappChoiceId(t)) return null;
  const m = /^btn_(\d+)$/i.exec(t);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  const list = [];
  for (let i = messageIndex - 1; i >= 0 && i > messageIndex - 25; i--) {
    const prev = allMessages[i];
    if (!prev || senderIsClient(prev)) continue;
    const labels = parseNumberedLabelsFromUraOverflowText(prev.text || "");
    if (labels.length) {
      list.push(...labels);
      break;
    }
  }
  if (!list.length) return null;
  const label = list[n];
  return label || null;
}

/**
 * @returns {{
 *   inboundChoice: { kind: string, title: string, id: string } | null,
 *   inboundHumanFromPayload: string,
 *   outboundOptions: { id: string, title: string, section?: string }[],
 *   numberedFallbackLabel: string | null,
 * }}
 */
export function buildWhatsappInteractivePresentation(message, messageIndex, allMessages) {
  const parsed = parseJsonMaybe(message?.json_whatsapp);
  const inboundChoice = metaInboundInteractiveChoice(parsed);
  const inboundHumanFromPayload = humanLabelFromApifacilPayload(parsed);

  let outboundOptions = extractMetaStyleOutboundOptions(parsed);
  if (!outboundOptions.length) outboundOptions = extractApifacilStyleButtons(parsed);

  const numberedFallback =
    typeof messageIndex === "number" && Array.isArray(allMessages)
      ? resolveChoiceFromPriorNumberedList(message, messageIndex, allMessages)
      : null;

  return {
    inboundChoice,
    inboundHumanFromPayload,
    outboundOptions,
    numberedFallbackLabel: numberedFallback,
  };
}
